import React from "react";
import { Tooltip } from "@mui/material";
import { Icon } from "@iconify/react";
import { useScreenHighlight } from "../../Hooks/ScreenHighlight";
import { HIGHLIGHT_COLORS, HIGHLIGHT_TOOLS } from "../../utils/highlightCoords";

const ToolButton = ({ tooltip, icon, active, onClick }) => (
  <Tooltip title={tooltip}>
    <button
      type="button"
      className={`screen-highlight-tool ${active ? "is-active" : ""}`}
      onClick={onClick}
    >
      <Icon icon={icon} fontSize={18} />
    </button>
  </Tooltip>
);

export default function HighlightToolbar() {
  const {
    tool,
    color,
    thickness,
    strokes,
    setTool,
    setColor,
    setThickness,
    closeToolbar,
    undoStroke,
    redoStroke,
    clearStrokes,
    canUndo,
    canRedo,
  } = useScreenHighlight();

  const showActions = canUndo || canRedo;

  return (
    <div className="screen-highlight-toolbar" role="toolbar" aria-label="Highlight tools">
      <ToolButton
        tooltip="Move (click the page)"
        icon="mdi:cursor-default"
        active={tool === HIGHLIGHT_TOOLS.NONE}
        onClick={() => setTool(HIGHLIGHT_TOOLS.NONE)}
      />
      <ToolButton
        tooltip="Laser"
        icon="mdi:laser-pointer"
        active={tool === HIGHLIGHT_TOOLS.LASER}
        onClick={() => setTool(HIGHLIGHT_TOOLS.LASER)}
      />
      <ToolButton
        tooltip="Pen"
        icon="mdi:pencil"
        active={tool === HIGHLIGHT_TOOLS.PEN}
        onClick={() => setTool(HIGHLIGHT_TOOLS.PEN)}
      />
      <ToolButton
        tooltip="Highlighter"
        icon="mdi:marker"
        active={tool === HIGHLIGHT_TOOLS.HIGHLIGHTER}
        onClick={() => setTool(HIGHLIGHT_TOOLS.HIGHLIGHTER)}
      />
      <span className="screen-highlight-toolbar__divider" aria-hidden />
      <ToolButton
        tooltip="Line — underline a line of text"
        icon="mdi:vector-line"
        active={tool === HIGHLIGHT_TOOLS.LINE}
        onClick={() => setTool(HIGHLIGHT_TOOLS.LINE)}
      />
      <ToolButton
        tooltip="Rectangle — box a word (Shift for square)"
        icon="mdi:rectangle-outline"
        active={tool === HIGHLIGHT_TOOLS.RECTANGLE}
        onClick={() => setTool(HIGHLIGHT_TOOLS.RECTANGLE)}
      />
      <ToolButton
        tooltip="Circle — oval around a word (Shift for perfect circle)"
        icon="mdi:circle-outline"
        active={tool === HIGHLIGHT_TOOLS.ELLIPSE}
        onClick={() => setTool(HIGHLIGHT_TOOLS.ELLIPSE)}
      />
      <ToolButton
        tooltip="Arrow"
        icon="mdi:arrow-top-right"
        active={tool === HIGHLIGHT_TOOLS.ARROW}
        onClick={() => setTool(HIGHLIGHT_TOOLS.ARROW)}
      />
      <span className="screen-highlight-toolbar__divider" aria-hidden />
      {HIGHLIGHT_COLORS.map((swatch) => (
        <Tooltip key={swatch} title="Color">
          <button
            type="button"
            className={`screen-highlight-color ${color === swatch ? "is-active" : ""}`}
            style={{ backgroundColor: swatch }}
            onClick={() => setColor(swatch)}
            aria-label={`Color ${swatch}`}
          />
        </Tooltip>
      ))}
      <span className="screen-highlight-toolbar__divider" aria-hidden />
      {["s", "m", "l"].map((size) => (
        <Tooltip key={size} title={size === "s" ? "Small" : size === "m" ? "Medium" : "Large"}>
          <button
            type="button"
            className={`screen-highlight-size ${thickness === size ? "is-active" : ""}`}
            onClick={() => setThickness(size)}
            aria-label={size === "s" ? "Small" : size === "m" ? "Medium" : "Large"}
          >
            <span className={`screen-highlight-size__dot is-${size}`} />
          </button>
        </Tooltip>
      ))}
      {showActions ? (
        <>
          <span className="screen-highlight-toolbar__divider" aria-hidden />
          {canUndo ? (
            <ToolButton
              tooltip="Undo"
              icon="mdi:undo"
              active={false}
              onClick={undoStroke}
            />
          ) : null}
          {canRedo ? (
            <ToolButton
              tooltip="Redo"
              icon="mdi:redo"
              active={false}
              onClick={redoStroke}
            />
          ) : null}
          {canUndo ? (
            <ToolButton
              tooltip="Clear all"
              icon="mdi:trash-can-outline"
              active={false}
              onClick={clearStrokes}
            />
          ) : null}
        </>
      ) : null}
      <span className="screen-highlight-toolbar__divider" aria-hidden />
      <ToolButton
        tooltip="Hide toolbar"
        icon="mdi:chevron-down"
        active={false}
        onClick={closeToolbar}
      />
      {strokes.length > 0 ? (
        <span className="screen-highlight-toolbar__count">{strokes.length}</span>
      ) : null}
    </div>
  );
}
